// Catálogo de fondos (wallpapers) + persistencia por usuario.
// Cada fondo es un gradiente base + 2 orbes. Elegible desde la TopBar, persiste
// en localStorage. Fallback = el wallpaper por defecto del rol.
import { useEffect, useState } from "react";

export interface Wallpaper {
  key: string;
  label: string;
  type?: "color" | "image";
  base: string;
  orbs: [string, string];
  swatch: [string, string, string];
  url?: string; // para type: "image"
}

export const WALLPAPERS: Record<string, Wallpaper> = {
  aurora: {
    key: "aurora",
    label: "Aurora",
    base: "radial-gradient(1200px 900px at 15% -5%, rgba(99,102,241,0.20), transparent 60%), radial-gradient(1000px 800px at 90% 110%, rgba(139,92,246,0.16), transparent 58%)",
    orbs: ["rgba(99,102,241,0.30)", "rgba(139,92,246,0.22)"],
    swatch: ["#6366F1", "#8B5CF6", "#a78bfa"],
  },
  forest: {
    key: "forest",
    label: "Bosque",
    base: "radial-gradient(1200px 900px at 15% -5%, rgba(16,185,129,0.18), transparent 60%), radial-gradient(1000px 800px at 90% 110%, rgba(13,148,136,0.14), transparent 58%)",
    orbs: ["rgba(16,185,129,0.26)", "rgba(13,148,136,0.20)"],
    swatch: ["#10B981", "#0D9488", "#34d399"],
  },
  ocean: {
    key: "ocean",
    label: "Océano",
    base: "radial-gradient(1200px 900px at 15% -5%, rgba(37,99,235,0.18), transparent 60%), radial-gradient(1000px 800px at 90% 110%, rgba(51,65,85,0.20), transparent 58%)",
    orbs: ["rgba(37,99,235,0.26)", "rgba(51,65,85,0.22)"],
    swatch: ["#2563EB", "#334155", "#60a5fa"],
  },
  sunset: {
    key: "sunset",
    label: "Atardecer",
    base: "radial-gradient(1200px 900px at 15% -5%, rgba(249,115,22,0.18), transparent 60%), radial-gradient(1000px 800px at 90% 110%, rgba(219,39,119,0.16), transparent 58%)",
    orbs: ["rgba(249,115,22,0.26)", "rgba(219,39,119,0.20)"],
    swatch: ["#f97316", "#db2777", "#fb923c"],
  },
  sakura: {
    key: "sakura",
    label: "Sakura",
    base: "radial-gradient(1200px 900px at 15% -5%, rgba(244,114,182,0.18), transparent 60%), radial-gradient(1000px 800px at 90% 110%, rgba(167,139,250,0.14), transparent 58%)",
    orbs: ["rgba(244,114,182,0.24)", "rgba(167,139,250,0.18)"],
    swatch: ["#f472b6", "#a78bfa", "#f9a8d4"],
  },
  mono: {
    key: "mono",
    label: "Grafito",
    base: "radial-gradient(1200px 900px at 15% -5%, rgba(120,120,135,0.14), transparent 60%), radial-gradient(1000px 800px at 90% 110%, rgba(80,80,95,0.16), transparent 58%)",
    orbs: ["rgba(140,140,155,0.18)", "rgba(90,90,105,0.16)"],
    swatch: ["#8b8b97", "#52525b", "#a1a1aa"],
  },

  // ── Fondos de IMAGEN (descargados en /public/wallpapers, servidos localmente) ──
  img_montana: { key: "img_montana", label: "Montaña", type: "image", url: "/wallpapers/montana.jpg", base: "", orbs: ["", ""], swatch: ["#475569", "#64748b", "#94a3b8"] },
  img_bosque: { key: "img_bosque", label: "Bosque (foto)", type: "image", url: "/wallpapers/bosque-real.jpg", base: "", orbs: ["", ""], swatch: ["#166534", "#15803d", "#4ade80"] },
  img_niebla: { key: "img_niebla", label: "Niebla", type: "image", url: "/wallpapers/niebla.jpg", base: "", orbs: ["", ""], swatch: ["#64748b", "#94a3b8", "#cbd5e1"] },
  img_cosmos: { key: "img_cosmos", label: "Cosmos", type: "image", url: "/wallpapers/cosmos.jpg", base: "", orbs: ["", ""], swatch: ["#1e1b4b", "#4338ca", "#818cf8"] },
  img_oceano: { key: "img_oceano", label: "Océano (foto)", type: "image", url: "/wallpapers/oceano-real.jpg", base: "", orbs: ["", ""], swatch: ["#0c4a6e", "#0284c7", "#38bdf8"] },
  img_estudio: { key: "img_estudio", label: "Estudio", type: "image", url: "/wallpapers/estudio.jpg", base: "", orbs: ["", ""], swatch: ["#78350f", "#b45309", "#fbbf24"] },
};

const STORAGE_KEY = "drnewenia.wallpaper";
const EVENT = "drnewenia:wallpaper-change";

/** Hook: devuelve [wallpaperKey, setWallpaperKey]. Default = el del rol. */
export function useWallpaper(roleDefault: string): [string, (k: string) => void] {
  const read = () => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v && WALLPAPERS[v] ? v : roleDefault;
    } catch {
      return roleDefault;
    }
  };
  const [key, setKey] = useState<string>(read);

  useEffect(() => {
    const sync = () => setKey(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleDefault]);

  const set = (k: string) => {
    if (!WALLPAPERS[k]) return;
    try {
      localStorage.setItem(STORAGE_KEY, k);
    } catch {
      /* noop */
    }
    setKey(k);
    window.dispatchEvent(new CustomEvent(EVENT));
  };

  return [key, set];
}
